import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarCard } from "@/components/CarCard";
import { QuickView } from "@/components/QuickView";
import { useFavorites } from "@/context/FavoritesContext";
import { Heart, Loader2, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { carPath } from "@/lib/carUrl";
import { useSiteContent, interpolateTemplate } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { LISTING_CAR_GRID, SITE_CONTAINER } from "@/lib/siteLayout";

export default function Favorites() {
  const { content } = useSiteContent();
  const fp = content.favorites_page || DEFAULT_SITE_CONTENT.favorites_page;
  const { favorites, priceDrops, ackPrices } = useFavorites();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickCar, setQuickCar] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);

  useEffect(() => {
    api
      .get("/cars")
      .then((res) => setCars(res.data))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, []);

  // Acknowledge price drops: refresh saved snapshots once the user sees this page
  useEffect(() => {
    if (!loading && cars.length > 0) ackPrices(cars);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const favCars = cars.filter((c) => favorites.includes(c.id));
  const dropCount = favCars.filter((c) => priceDrops[c.id]).length;

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <section className="pt-28 pb-20">
        <div className={SITE_CONTAINER}>
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-red-400" fill="currentColor" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl md:text-4xl font-light">{fp.title}</h1>
          </div>
          <p className="text-slate-400 mt-2 text-sm">{fp.subtitle}</p>

          {dropCount > 0 && (
            <div
              data-testid="price-drop-banner"
              className="glass rounded-2xl p-4 mt-8 flex items-center gap-3 border border-emerald-400/30"
            >
              <TrendingDown className="w-5 h-5 text-emerald-400 shrink-0" strokeWidth={1.5} />
              <div className="text-sm">
                <span className="text-emerald-400 font-medium">
                  {dropCount === 1 ? fp.price_drop_one : interpolateTemplate(fp.price_drop_many, { count: dropCount })}
                </span>
                <span className="text-slate-400">{fp.price_drop_suffix}</span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-electric animate-spin" /></div>
          ) : favCars.length === 0 ? (
            <div data-testid="favorites-empty" className="glass rounded-3xl p-14 mt-10 text-center">
              <Heart className="w-10 h-10 text-slate-500 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-slate-300">{fp.empty_title}</p>
              <p className="text-sm text-slate-400 mt-1">{fp.empty_body}</p>
              <Link
                to="/#keszlet"
                data-testid="favorites-browse"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all"
              >
                {fp.browse_label}
              </Link>
            </div>
          ) : (
            <div data-testid="favorites-grid" className={`${LISTING_CAR_GRID} mt-10`}>
              {favCars.map((car) => (
                <CarCard
                  key={car.id}
                  car={car}
                  compact
                  priceDrop={priceDrops[car.id]}
                  onQuickView={(c) => { setQuickCar(c); setQuickOpen(true); }}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
      <QuickView car={quickCar} open={quickOpen} onClose={() => setQuickOpen(false)} />
    </div>
  );
}
